/**
 * mount.tsx — the one exported way to put the app into a DOM node.
 *
 * Split away from `main.tsx` for two reasons, both of which outlive the skeleton:
 *
 *  1. `main.tsx` mounts as a **module-level side effect**, so importing it from a test
 *     mounts once, into whatever the document happens to hold, and can never be
 *     repeated or pointed at a container of the test's choosing. Everything worth
 *     asserting therefore lives here, and `apps/web/test/mount.test.tsx` imports only
 *     this file.
 *  2. Coverage. The v8 provider reports only the files a test actually loaded, so an
 *     entry point nobody imports never enters the report. Keeping `main.tsx` empty of
 *     logic is what makes that safe: were the mounting code in there, either the tests
 *     would import it (mounting on import) or it would sit in the report at zero and
 *     drag the 55 % line threshold down.
 *
 * `StrictMode` is on from the first commit rather than added later: it double-invokes
 * effects in development, which surfaces missing cleanup in the player wiring (tasks
 * 04 and 07) at the moment that code is written instead of after it ships.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';

import { App } from './App';

/**
 * The container `index.html` provides. Exported so the test can assert against the
 * same string the HTML uses instead of repeating a literal that could drift.
 */
export const ROOT_ELEMENT_ID = 'root';

/**
 * Renders the app into `container`, or into `#root` when called without one.
 *
 * Returns the React root so a caller can unmount — the tests need it, and so will the
 * error boundary work in task 04. A missing container throws instead of returning
 * silently: a Mini App that renders nothing and reports nothing is the hardest
 * possible thing to diagnose from a phone. That message stays English — it can only
 * fire when this file and `index.html` disagree, which is a developer's problem and
 * never reaches a screen; the Russian strings are the ones the listener reads.
 */
export function mountApp(container?: Element): Root {
  const target = container ?? document.getElementById(ROOT_ELEMENT_ID);
  if (target === null) {
    throw new Error(
      `mount: no #${ROOT_ELEMENT_ID} element in the document — index.html must provide one.`,
    );
  }

  const root = createRoot(target);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
  return root;
}
